import createApiClient from '@/api/axiosInstance';
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  FileText,
  Image,
  ImageIcon,
  Loader2,
  Trash2,
  Upload,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Simplified FileUploadBox for Modal Use Only
export const FileUploadBox = React.memo(
  forwardRef(
    (
      {
        DOCUMENT_TITLES,
        MAX_FILE_SIZE,
        ACCEPTED_FILE_TYPES,
        id,
        label,
        documentType,
        existingDocument,
        existingDocuments,
        setExistingDocuments,
        documentStates,
        setDocumentStates,
        uploadProgress,
        setUploadProgress,
        isUploading,
        setIsUploading,
        setError,
        isUploadingCertificate = false,
        editState = true,
        onFileSelect, // New prop to handle file selection without upload
        selectedFile, // New prop to receive the selected file from parent
      },
      ref,
    ) => {
      if (!editState) {
        console.log('FALSEEDIT STATE');
      }
      const token = useSelector((state) => state.authReducer.token);
      const file = selectedFile;
      const progress = uploadProgress[documentType];
      const [displayProgress, setDisplayProgress] = useState(0);
      const animationFrameRef = useRef(null);
      const startProgressRef = useRef(0);
      const targetProgressRef = useRef(0);
      const startTimeRef = useRef(null);

      const navigate = useNavigate();
      const dispatch = useDispatch();
      const apiClient = createApiClient(Navigate, dispatch);

      useEffect(() => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        startProgressRef.current = displayProgress;
        targetProgressRef.current = progress;
        startTimeRef.current = performance.now();

        const animate = (currentTime) => {
          const elapsed = currentTime - startTimeRef.current;
          const duration = 500;

          const t = Math.min(elapsed / duration, 1);
          const easedT =
            t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

          const newProgress =
            startProgressRef.current +
            (targetProgressRef.current - startProgressRef.current) * easedT;

          setDisplayProgress(newProgress);

          if (elapsed < duration) {
            animationFrameRef.current = requestAnimationFrame(animate);
          } else {
            setDisplayProgress(targetProgressRef.current);
          }
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
        };
      }, [progress]);

      const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
          if (selectedFile.size > MAX_FILE_SIZE) {
            setError('File size exceeds 1MB');
            return;
          }
          if (!ACCEPTED_FILE_TYPES.includes(selectedFile.type)) {
            setError('Invalid file type');
            return;
          }

          // Only set the file selection for modal use
          if (onFileSelect) {
            onFileSelect(selectedFile);
          }
        }
      };

      const renderFilePreview = (fileToPreview) => {
        if (fileToPreview?.type?.startsWith('image/')) {
          return <Image className="h-8 w-8 text-gray-400" />;
        } else if (fileToPreview?.type === 'application/pdf') {
          return <FileText className="h-8 w-8 text-gray-400" />;
        } else {
          return <ImageIcon className="h-8 w-8 text-gray-400" />;
        }
      };

      const fetchExistingDocuments = async () => {
        try {
          const response = await apiClient.get('/api/documents/get');

          if (response.status === 200) {
            setExistingDocuments(response.data.data || []);
          }
          if (response.status === 401) {
            setError('Unauthorized');
          }
          if (response.status === 500) {
            setError('Internal Server Error');
            console.log('Internal Server Error');
          }
          if (response.status !== 200) {
            setError('Failed to fetch documents');
          }
        } catch (err) {
          console.error(
            'Error fetching documents:',
            err.response.data.error_message,
          );
          setError(
            `Unauthorized! ${err.response.data.error_message}` ||
              'Failed to fetch documents',
          );
        }
      };

      // Modified to accept fileName parameter
      const handleIndividualUpload = async (
        file,
        documentName,
        fileName,
        authority,
        certificateNumber,
      ) => {
        setIsUploading(true);
        setError('');

        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('document-name', documentName);
          formData.append('file-name', fileName);
          formData.append('issuing-authority', authority);
          formData.append('certificate-number', certificateNumber);

          const uploadResponse = await apiClient.post(
            '/api/documents/upload',
            formData,
            {
              headers: {
                'x-auth-token': token,
                'Content-Type': 'multipart/form-data',
              },
              onUploadProgress: (progressEvent) => {
                console.log(
                  `Upload progress for ${documentName}:`,
                  Math.round(
                    (progressEvent.loaded / progressEvent.total) * 100,
                  ),
                );
                setUploadProgress((prev) => ({
                  ...prev,
                  [documentName]: progressEvent.total
                    ? Math.round(
                        (progressEvent.loaded / progressEvent.total) * 100,
                      )
                    : 0,
                }));
              },
            },
          );

          if (uploadResponse.status !== 200) {
            throw new Error('Failed to upload files');
          }

          const uploadResult = uploadResponse.data;
          if (!uploadResult.success) {
            throw new Error(uploadResult.error_message || 'File upload failed');
          }

          const newDocumentData = uploadResult.data;

          fetchExistingDocuments();
          setExistingDocuments((prev) => [
            ...prev.filter((doc) => doc.document_name !== documentName),
            newDocumentData,
          ]);

          setDocumentStates((prev) => ({
            ...prev,
            [documentName]: file,
          }));

          setUploadProgress((prev) => ({
            ...prev,
            [documentName]: 100,
          }));
        } catch (err) {
          console.error(`Error uploading ${documentName}:`, err);
          setError(
            err.response.data.error_message || 'Failed to upload document',
          );
        } finally {
          setIsUploading(false);

          setTimeout(() => {
            setUploadProgress((prev) => ({
              ...prev,
              [documentName]: 0,
            }));
          }, 300);
        }
      };

      // Expose the upload function to parent component
      useImperativeHandle(ref, () => ({
        uploadFile: (fileName, authority = '', certificateNumber = '') =>
          handleIndividualUpload(
            selectedFile || file,
            documentType,
            fileName,
            authority,
            certificateNumber,
          ),
      }));

      return (
        <div className="font-roboto">
          <div className="relative group justify-center items-center flex flex-col">
            {file ? (
              <div className="relative flex flex-col justify-center items-center w-64 h-36 border border-lightPurple hover:border-lightPurple ease-in duration-150 hover:border-opacity-70 border-opacity-30 rounded-md overflow-hidden">
                <div className="w-full h-full flex items-center justify-center">
                  {renderFilePreview(file)}
                </div>
                <span className="text-xs text-center p-2 truncate max-w-full absolute bottom-0 w-full bg-lightPurple bg-opacity-60 text-white backdrop-filter backdrop-blur-sm">
                  {file.name}
                </span>
                {editState && (
                  <button
                    type="button"
                    onClick={() => onFileSelect && onFileSelect(null)}
                    className="absolute top-1 right-1 bg-purple-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <Label
                htmlFor={id}
                className="w-64 h-36 border-2 border-dashed border-gray-400 rounded-md flex flex-col items-center justify-center cursor-pointer text-muted-foreground hover:text-darkPurple hover:border-darkerPurple ease-in duration-200"
              >
                {isUploading && progress ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-center">Uploading...</span>
                    {progress > 0 && (
                      <div className="relative w-3/4 justify-center h-1 bg-gray-200 rounded-md mt-2 ">
                        <div
                          className="absolute h-1 bg-lightPurple rounded-md transition-all duration-300"
                          style={{ width: `${displayProgress}%` }}
                        ></div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 mx-2 w-4" />
                    <span className="text-sm text-center">{label}</span>
                    <Input
                      id={id}
                      type="file"
                      className="hidden"
                      accept={ACCEPTED_FILE_TYPES.join(',')}
                      onChange={handleFileChange}
                    />
                  </>
                )}
              </Label>
            )}
          </div>
        </div>
      );
    },
  ),
);

export const fetchExistingDocuments = async (
  apiClient,
  setExistingDocuments,
  setError,
) => {
  try {
    const response = await apiClient.get('/api/documents/get');

    if (response.status === 200) {
      setExistingDocuments(response.data.data || []);
      setError('');
    }
    if (response.status === 401) {
      setError('Unauthorized');
    }
    if (response.status === 500) {
      setError('Internal Server Error');
      console.log('Internal Server Error');
    }
    if (response.status !== 200) {
      setError('Failed to fetch documents');
    }
  } catch (err) {
    console.error('Error fetching documents:', err.response.data.error_message);
    setError(
      `Unauthorized! ${err.response.data.error_message}` ||
        'Failed to fetch documents',
    );
  }
};

export const fetchExistingDocumentsArray = async (
  documentIds = [],
  apiClient,
  setExistingDocuments,
  setError,
) => {
  if (documentIds.length > 0) {
    try {
      const response = await apiClient.post(
        '/api/documents/get/by-ids',
        JSON.stringify({
          document_ids: documentIds,
        }),
      );

      if (response.status === 200) {
        setExistingDocuments(response.data.data || []);
        setError('');
      } else if (response.status === 401) {
        setError('Unauthorized');
      } else if (response.status === 500) {
        setError('Internal Server Error');
        console.log('Internal Server Error');
      } else {
        setError('Failed to fetch documents');
      }
    } catch (err) {
      console.error(
        'Error fetching documents:',
        err?.response?.data?.error_message || err.message,
      );
      setError(
        `Unauthorized! ${err?.response?.data?.error_message}` ||
          'Failed to fetch documents',
      );
    }
  }
};
